import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { type NavMenuHierarchyL1, type NavMenuHierarchyL2 } from '../nav/utils/NavMenuHierarchy'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import { useAbsoluteRouteMatch } from '../../../utils/routing/useAbsoluteRouteMatch.hooks'
import { url as resolveUrlPattern } from '../../../utils/parsing/url'
import { toSentenceCase } from '../../../utils/strings/toSentenceCase'
import { type AccessControlConfig } from '../../../legacy/components/AccessControl/accessControl.utils'
import { type SpotlightActionV3 } from './searchV3.types'
import { useNavHierarchyRetailerV3 } from './useNavHierarchyRetailerDataV3'
import { useNavHierarchyAdminV3 } from './useNavHierarchyAdminDataV3'
import { useNavHierarchySettingsV3 } from './useNavHierarchySettingsV3'
import { type NavMenuHierarchyV3 } from './navSectionV3'
import { type NavBadgeContext } from './nav-badges/navBadge.types'
import { PREVIEW_RELEASE_BADGE_ID, resolveNavBadges } from './nav-badges/navBadgeRules'
import { useNavBadgeContext } from './nav-badges/useNavBadges'

type Resolved = { href: string; isExternal: boolean }

// Inline equivalent of `useResolveNavHref`, but pulled out of React so it can
// be applied to many leaves in a single pass without violating rules-of-hooks.
// Returns undefined when:
//   - no route or href is provided
//   - the route name isn't registered in routesByName
//
// Routes whose pattern requires params not in the current URL match still
// resolve and are surfaced — `resolveUrlPattern` stringifies missing params
// as the literal "undefined". This matches v2 search behavior: the entry is
// discoverable even if clicking it lands on a broken URL.
const resolveLeafHref = (
  item: { route?: string; href?: string; newTab?: boolean },
  routesByName: Record<string, string>,
  params: Record<string, string>
): Resolved | undefined => {
  if (item.href) return { href: item.href, isExternal: true }
  if (!item.route) return undefined

  const pattern = routesByName[item.route]
  if (!pattern) return undefined

  const href = resolveUrlPattern(pattern, params)
  return { href, isExternal: Boolean(item.newTab) }
}

// Resolves the v2 alias label for a leaf. Returns undefined when:
//   - the entry never declared a distinct v3 label (`v2LabelId` is unset), or
//   - the v2 label resolves to the same display string as the v3 label
//     (rare, but defensible: nothing to clarify if the strings match).
const resolveV2Label = (
  v2LabelId: string | undefined,
  v3Label: string,
  formatLabel: (id: string) => string
): string | undefined => {
  if (!v2LabelId) return undefined
  const v2Label = toSentenceCase(formatLabel(v2LabelId))
  return v2Label === v3Label ? undefined : v2Label
}

type WalkContext = {
  routesByName: Record<string, string>
  params: Record<string, string>
  navigate: (resolved: Resolved) => () => void
  formatLabel: (id: string) => string
  badgeContext: NavBadgeContext
  out: SpotlightActionV3[]
}

const isPreviewEntry = (
  item: { accessControl?: AccessControlConfig | AccessControlConfig[] },
  badgeContext: NavBadgeContext
): boolean =>
  resolveNavBadges(item, badgeContext).some(badge => badge.id === PREVIEW_RELEASE_BADGE_ID)

// Walks an L1 subtree and yields every leaf with a `route` or `href` that
// resolves successfully. Group label = the L1's i18n'd name.
//
// Handles two L1 shapes:
//   - Leaf L1 (own route/href, no children) — emit one entry, label = group.
//   - Group L1 — recurse children. Intermediate L2s with their own children
//     (`hasL2Children` shape from v2) are recursed through; leaf L2/L3s are
//     emitted with their own i18n'd name as the label.
const walkHierarchyL1 = (l1: NavMenuHierarchyL1, groupLabel: string, ctx: WalkContext) => {
  const emit = (
    label: string,
    item: {
      route?: string
      href?: string
      newTab?: boolean
      v2LabelId?: string
      accessControl?: AccessControlConfig | AccessControlConfig[]
    }
  ) => {
    const resolved = resolveLeafHref(item, ctx.routesByName, ctx.params)
    if (!resolved) return
    ctx.out.push({
      group: groupLabel,
      label,
      v2Label: resolveV2Label(item.v2LabelId, label, ctx.formatLabel),
      isPreview: isPreviewEntry(item, ctx.badgeContext),
      route: item.route,
      href: item.href,
      onClick: ctx.navigate(resolved),
    })
  }

  const l1HasChildren = (l1.children?.length ?? 0) > 0
  if (!l1HasChildren && (l1.route || l1.href)) {
    emit(groupLabel, l1)
    return
  }

  const recurse = (node: NavMenuHierarchyL2) => {
    const hasChildren = (node.children?.length ?? 0) > 0
    if (hasChildren) {
      node.children?.forEach(recurse)
      return
    }
    if (!node.route && !node.href) return
    emit(toSentenceCase(ctx.formatLabel(node.name)), node)
  }

  l1.children?.forEach(recurse)
}

// Pure helper: walks every L1 in a v3 hierarchy and pushes one SpotlightAction
// per reachable leaf into `ctx.out`. Shared between the retailer and admin
// search hooks below so they walk identically — only the source hierarchy
// differs.
const collectActionsFromHierarchy = (hierarchy: NavMenuHierarchyV3, ctx: WalkContext): void => {
  hierarchy.sections.forEach(section => {
    section.l1s.forEach(l1 => {
      const groupLabel = ctx.formatLabel(l1.name)
      walkHierarchyL1(l1, groupLabel, ctx)
    })
  })
}

// Hook that returns SpotlightActionV3[] for the v3 nav: every reachable leaf
// in the retailer hierarchy plus every reachable item in the settings sidebar.
// Both upstream hooks are already access-filtered, so we don't reapply
// permissions here.
//
// Intentional duplicates are kept: a route attached to multiple L1s (e.g.
// "Segments" under both Marketplace and Storefront) emits one entry per L1,
// and routes that exist in both the retailer hierarchy and the settings
// sidebar emit one entry under each. The L1 / settings group header
// disambiguates the navigation context for the user.
//
// Resolution caveats: leaves whose route requires a URL param not in the
// current routeMatch are still surfaced for discoverability (matching v2
// search). Their resolved href will contain the literal "undefined" for
// missing segments and may navigate to a broken page (see resolveLeafHref).
export const useV3SearchActions = (): SpotlightActionV3[] => {
  const intl = useIntl()
  const history = useHistory()
  const { routesByName } = useCoreContext()
  const routeMatch = useAbsoluteRouteMatch()
  const { navMenuHierarchy } = useNavHierarchyRetailerV3()
  const { sections: settingsSections } = useNavHierarchySettingsV3()
  const badgeContext = useNavBadgeContext()

  return useMemo(() => {
    const params = (routeMatch?.params as Record<string, string>) ?? {}
    const out: SpotlightActionV3[] = []

    const navigate = (resolved: Resolved) => () => {
      if (resolved.isExternal) {
        window.open(resolved.href, '_blank', 'noopener,noreferrer')
      } else {
        history.push(resolved.href)
      }
    }

    const ctx = {
      routesByName,
      params,
      navigate,
      formatLabel: (id: string) => intl.formatMessage({ id }),
      badgeContext,
      out,
    }

    collectActionsFromHierarchy(navMenuHierarchy, ctx)

    settingsSections.forEach(section => {
      const groupLabel = intl.formatMessage({ id: section.name })
      section.items.forEach(item => {
        if (!item.route) return
        const resolved = resolveLeafHref(item, routesByName, params)
        if (!resolved) return
        out.push({
          group: groupLabel,
          label: toSentenceCase(intl.formatMessage({ id: item.name })),
          route: item.route,
          isPreview: isPreviewEntry(item, badgeContext),
          onClick: navigate(resolved),
        })
      })
    })

    return out
  }, [
    navMenuHierarchy,
    settingsSections,
    routesByName,
    routeMatch?.params,
    history,
    intl,
    badgeContext,
  ])
}

// Admin counterpart of `useV3SearchActions`. Walks the admin v3 hierarchy
// (the 8 L1s assembled by `useNavHierarchyAdminV3`) and emits one
// SpotlightAction per reachable leaf. No settings sidebar — admin doesn't
// mount one — so the returned set is exactly the admin nav surface.
export const useV3SearchActionsAdmin = (): SpotlightActionV3[] => {
  const intl = useIntl()
  const history = useHistory()
  const { routesByName } = useCoreContext()
  const routeMatch = useAbsoluteRouteMatch()
  const { navMenuHierarchy } = useNavHierarchyAdminV3()
  const badgeContext = useNavBadgeContext()

  return useMemo(() => {
    const params = (routeMatch?.params as Record<string, string>) ?? {}
    const out: SpotlightActionV3[] = []

    const navigate = (resolved: Resolved) => () => {
      if (resolved.isExternal) {
        window.open(resolved.href, '_blank', 'noopener,noreferrer')
      } else {
        history.push(resolved.href)
      }
    }

    const ctx = {
      routesByName,
      params,
      navigate,
      formatLabel: (id: string) => intl.formatMessage({ id }),
      badgeContext,
      out,
    }

    collectActionsFromHierarchy(navMenuHierarchy, ctx)

    return out
  }, [navMenuHierarchy, routesByName, routeMatch?.params, history, intl, badgeContext])
}
